/**
 * `[anudan_portal_subscriptions]` — WP-31's recurring-management screen.
 *
 * The first script the donor portal has needed (`Frontend\Portal\PortalSubscriptionsRenderer`'s
 * own docblock: pause/resume/change-amount/cancel/update-payment-method are state changes a plain
 * `<form method="get">` cannot express — WordPress's own cookie-authenticated REST calls require
 * a nonce header, and updating a payment method additionally requires a gateway-hosted field,
 * which by definition cannot exist as a plain form input (CLAUDE.md rule 5).
 *
 * ## Every figure is the server's own, never computed here (CLAUDE.md rule 2)
 *
 * This module reads `data-anudan-*` attributes the server already rendered and relays whatever
 * a mutating route's own response says changed — it never itself decides a resume date, a next
 * cycle, or an amount. After any successful action the page reloads (`window.location.reload()`)
 * rather than this script re-rendering a row from the mutation response, so the table a donor
 * sees next is always the server's own render, not a client-side approximation of it.
 *
 * ## Every action confirmed before execution, confirmed after with what changed and when
 *
 * Pause, change-amount and cancel open a native `<dialog>` (focus-trapped, `Escape`-dismissible,
 * for free, by the browser) before anything is sent. Resume needs no extra input, so a native
 * `window.confirm()` stands in for the same "confirmed before execution" requirement without a
 * dialog that would otherwise be empty. Every action's own success message — worded server-side,
 * by the REST route it called — is what the live region announces after.
 */

import { loadGateway } from './checkout/gateway-loader.js';
import { plainFailureMessage, reportFailure } from './payment-method-failure.js';
import { readConfig } from './shared/config-block.js';
import { stripeElementsOptions, paymentElementOptions } from './shared/brand.js';
import { whenReady } from './shared/stripe-ready.js';

readConfig( 'anudan-portal-subscriptions' );

function config() {
	return ( window.AnudanEngine && window.AnudanEngine.config ) || {};
}

function restRoot() {
	return config().restUrl || '/wp-json/anudan/v1/';
}

/**
 * @param {string} path
 * @param {object} [body]
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
async function post( path, body ) {
	const response = await fetch( restRoot() + path, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config().nonce || '',
		},
		credentials: 'same-origin',
		body: JSON.stringify( body || {} ),
	} );

	let parsed = null;

	try {
		parsed = await response.json();
	} catch ( error ) {
		parsed = null;
	}

	return { ok: response.ok, status: response.status, body: parsed };
}

function t( text ) {
	return window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text;
}

/** Where a confirmation waits for the reload that follows a successful action (this script's own key). */
const NOTICE_KEY = 'anudanPortalSubscriptionsNotice';

class PortalSubscriptions {
	/**
	 * @param {HTMLElement} root
	 */
	constructor( root ) {
		this.root = root;
		this.statusRegion = root.querySelector( '[data-anudan-subscriptions-status]' );
		this.restoreNotice();
		this.dialogs = {};

		root.querySelectorAll( '[data-anudan-dialog]' ).forEach( ( dialog ) => {
			this.dialogs[ dialog.dataset.anudanDialog ] = dialog;
			this.wireDialog( dialog );
		} );

		root.querySelectorAll( '[data-anudan-subscription-row]' ).forEach( ( row ) => {
			row.querySelectorAll( '[data-anudan-action]' ).forEach( ( button ) => {
				button.addEventListener( 'click', () => this.handleAction( row, button.dataset.anudanAction ) );
			} );
		} );
	}

	/**
	 * @param {HTMLDialogElement} dialog
	 */
	wireDialog( dialog ) {
		const cancelButton = dialog.querySelector( '[data-anudan-dialog-cancel]' );

		if ( cancelButton ) {
			cancelButton.addEventListener( 'click', () => dialog.close() );
		}

		const form = dialog.querySelector( 'form[data-anudan-form]' );

		if ( form ) {
			form.addEventListener( 'submit', ( event ) => {
				event.preventDefault();
				this.submitForm( dialog, form );
			} );
		}
	}

	/**
	 * @param {HTMLElement} row
	 * @param {string}      action
	 */
	async handleAction( row, action ) {
		const uid = row.dataset.anudanSubscriptionUid;

		if ( 'resume' === action ) {
			if ( ! window.confirm( t( 'Resume this recurring gift now?' ) ) ) {
				return;
			}

			const response = await post( `portal/subscriptions/${ encodeURIComponent( uid ) }/resume`, {} );

			this.announce( response );

			if ( response.ok ) {
				window.location.reload();
			}

			return;
		}

		if ( 'payment-method' === action ) {
			// PayPal holds the funding source in the donor's own PayPal account: explain and link there
			// instead of opening a card form this site could not honour.
			if ( 'paypal' === row.dataset.anudanSubscriptionGateway && this.dialogs[ 'payment-method-paypal' ] ) {
				this.dialogs[ 'payment-method-paypal' ].showModal();

				return;
			}

			this.openPaymentMethodDialog( row );

			return;
		}

		const dialog = this.dialogs[ action ];

		if ( ! dialog ) {
			return;
		}

		dialog.dataset.anudanActiveUid = uid;
		this.clearDialogError( dialog );
		this.fill( dialog, row );

		const amountNote = dialog.querySelector( '[data-anudan-amount-note]' );

		if ( amountNote ) {
			const next = row.dataset.anudanSubscriptionNext || '';

			amountNote.textContent = '' !== next
				? t( 'The new amount starts with your next payment on %s.' ).replace( '%s', next )
				: t( 'The new amount starts with your next payment.' );
		}

		// A PayPal gift needs PayPal's own approval of the new amount: say so before the button is pressed.
		const paypalNote = dialog.querySelector( '[data-anudan-amount-paypal-note]' );

		if ( paypalNote ) {
			paypalNote.hidden = 'paypal' !== row.dataset.anudanSubscriptionGateway;
		}

		dialog.showModal();
	}

	/**
	 * P1 (0.9.40): a dialog names the gift it acts on, "Your gift of USD 15.00 to the General Fund", from the card the button was
	 * on. Text only: nothing here is read as markup.
	 *
	 * @param {HTMLElement} dialog
	 * @param {HTMLElement} row
	 */
	fill( dialog, row ) {
		const values = {
			amount: row.dataset.anudanSubscriptionAmount || '',
			every: row.dataset.anudanSubscriptionEvery || '',
			fund: row.dataset.anudanSubscriptionFund || '',
			currency: row.dataset.anudanSubscriptionCurrency || '',
			card: row.dataset.anudanSubscriptionCard || '',
		};

		dialog.querySelectorAll( '[data-anudan-fill]' ).forEach( ( slot ) => {
			slot.textContent = values[ slot.getAttribute( 'data-anudan-fill' ) ] || '';
		} );
	}

	/**
	 * @param {HTMLDialogElement} dialog
	 * @param {HTMLFormElement}   form
	 */
	async submitForm( dialog, form ) {
		const uid = dialog.dataset.anudanActiveUid;
		const action = dialog.dataset.anudanDialog;
		const data = new FormData( form );

		let path = '';
		let body = {};

		if ( 'pause' === action ) {
			path = `portal/subscriptions/${ encodeURIComponent( uid ) }/pause`;
			body = { cycles: parseInt( String( data.get( 'cycles' ) || '1' ), 10 ), reason: String( data.get( 'reason' ) || '' ) };
		} else if ( 'amount' === action ) {
			path = `portal/subscriptions/${ encodeURIComponent( uid ) }/amount`;
			// `return_to` is only the page to come back to after PayPal; the server builds the address.
			body = { gift_amount: String( data.get( 'gift_amount' ) || '' ), return_to: window.location.pathname };
		} else if ( 'cancel' === action ) {
			path = `portal/subscriptions/${ encodeURIComponent( uid ) }/cancel`;
			body = { reason: String( data.get( 'reason' ) || '' ) };
		} else {
			return;
		}

		const response = await post( path, body );

		if ( ! response.ok ) {
			this.showDialogError( dialog, ( response.body && response.body.message ) || t( 'Something went wrong. Please try again.' ) );

			return;
		}

		dialog.close();
		this.announce( response );

		// PayPal needs the donor to approve the new amount on its own site. Nothing has changed yet.
		const approvalUrl = response.body && response.body.approval_url;

		if ( 'string' === typeof approvalUrl && /^https:\/\/([a-z0-9-]+\.)*paypal\.com\//i.test( approvalUrl ) ) {
			window.location.assign( approvalUrl );

			return;
		}

		window.location.reload();
	}

	/**
	 * @param {HTMLElement} row
	 */
	async openPaymentMethodDialog( row ) {
		const dialog = this.dialogs[ 'payment-method' ];

		if ( ! dialog ) {
			return;
		}

		const uid = row.dataset.anudanSubscriptionUid;

		dialog.dataset.anudanActiveUid = uid;
		this.clearDialogError( dialog );
		this.fill( dialog, row );

		const mountPoint = dialog.querySelector( '[data-anudan-payment-element]' );
		const confirmButton = dialog.querySelector( '[data-anudan-dialog-confirm]' );

		mountPoint.textContent = t( 'Loading…' );
		confirmButton.disabled = true;

		const intentResponse = await post( `portal/subscriptions/${ encodeURIComponent( uid ) }/setup-intent`, {} );

		if ( ! intentResponse.ok || ! intentResponse.body ) {
			mountPoint.textContent = '';
			this.showDialogError( dialog, t( 'We could not load the payment form. Please try again.' ) );
			dialog.showModal();

			return;
		}

		const gatewayGlobal = await loadGateway( intentResponse.body.gateway );

		if ( ! gatewayGlobal ) {
			mountPoint.textContent = '';
			this.showDialogError( dialog, t( 'We could not load the payment form. Please refresh and try again.' ) );
			dialog.showModal();

			return;
		}

		const publishableKey = intentResponse.body.public_config && intentResponse.body.public_config.publishable_key;
		const stripe = gatewayGlobal( publishableKey );
		const elements = stripe.elements( stripeElementsOptions( { clientSecret: intentResponse.body.client_token } ) );
		const paymentElement = elements.create( 'payment', paymentElementOptions() );

		mountPoint.textContent = '';
		paymentElement.mount( mountPoint );
		// After rc7: pressable only once Stripe's card form is ready, as on "Update your card" (`shared/stripe-ready.js`).
		whenReady( paymentElement, confirmButton, dialog, () => this.showDialogError( dialog, t( 'We could not load the payment form. Please refresh and try again.' ) ) );

		confirmButton.onclick = async () => {
			confirmButton.disabled = true;
			this.clearDialogError( dialog );

			const confirmation = await stripe.confirmSetup( { elements, redirect: 'if_required' } );

			if ( confirmation.error ) {
				confirmButton.disabled = false;
				this.showDialogError( dialog, plainFailureMessage( confirmation.error ) );
				reportFailure( restRoot(), `portal/subscriptions/${ encodeURIComponent( uid ) }/payment-method-failed`, { 'X-WP-Nonce': config().nonce || '' } );

				return;
			}

			const paymentMethodId = confirmation.setupIntent && confirmation.setupIntent.payment_method;

			const updateResponse = await post(
				`portal/subscriptions/${ encodeURIComponent( uid ) }/payment-method`,
				{ payment_method_id: paymentMethodId }
			);

			if ( ! updateResponse.ok ) {
				confirmButton.disabled = false;
				this.showDialogError( dialog, ( updateResponse.body && updateResponse.body.message ) || t( 'We could not save this card.' ) );

				return;
			}

			dialog.close();
			this.announce( updateResponse );
			window.location.reload();
		};

		dialog.showModal();
	}

	/**
	 * @param {HTMLDialogElement} dialog
	 * @param {string}            message
	 */
	showDialogError( dialog, message ) {
		const errorEl = dialog.querySelector( '[data-anudan-dialog-error]' );

		if ( errorEl ) {
			errorEl.textContent = message;
			errorEl.hidden = false;
		}
	}

	/**
	 * @param {HTMLDialogElement} dialog
	 */
	clearDialogError( dialog ) {
		const errorEl = dialog.querySelector( '[data-anudan-dialog-error]' );

		if ( errorEl ) {
			errorEl.textContent = '';
			errorEl.hidden = true;
		}
	}

	/**
	 * @param {{ok: boolean, body: any}} response
	 */
	announce( response ) {
		const message = ( response.body && response.body.message ) || '';

		if ( this.statusRegion ) {
			this.statusRegion.textContent = message;
		}

		// A success reloads the page at once, which would take the message with it: it is kept for the page that loads next.
		if ( response.ok && message ) {
			try {
				window.sessionStorage.setItem( NOTICE_KEY, message );
			} catch ( error ) {
				// Storage refused (a private window): the page still shows the result, only the line is lost.
			}
		}
	}

	/**
	 * Shows, once, the confirmation the page before the reload kept.
	 */
	restoreNotice() {
		let message = '';

		try {
			message = window.sessionStorage.getItem( NOTICE_KEY ) || '';
			window.sessionStorage.removeItem( NOTICE_KEY );
		} catch ( error ) {
			message = '';
		}

		// A line the server wrote into the page (the way back from PayPal's approval page says what became of the change) is the
		// truer one: the kept message is shown only where the page has none, or it would put a stale "approve it at PayPal" over it.
		if ( message && this.statusRegion && '' === ( this.statusRegion.textContent || '' ).trim() ) {
			this.statusRegion.textContent = message;
		}
	}
}

document.querySelectorAll( '[data-anudan-portal-subscriptions]' ).forEach( ( root ) => {
	new PortalSubscriptions( root );
} );
